import {
    Component,
    Input,
    OnInit,
    ElementRef,
    HostListener,
    ChangeDetectionStrategy,
    inject,
    output,
    viewChild,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { formatDate } from '@progress/kendo-angular-intl';
import {
    DateRangeComponent,
    DateRangePopupComponent,
    DateRangePopupTemplateDirective,
    MultiViewCalendarComponent,
} from '@progress/kendo-angular-dateinputs';
import { FloatingLabelComponent } from '@progress/kendo-angular-label';
import { TextBoxDirective } from '@progress/kendo-angular-inputs';

@Component({
    selector: 'app-date-range-filter',
    templateUrl: './date-range-filter.component.html',
    styleUrls: ['./date-range-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        DateRangeComponent,
        FloatingLabelComponent,
        TextBoxDirective,
        DateRangePopupComponent,
        DateRangePopupTemplateDirective,
        MultiViewCalendarComponent,
    ],
})
export class DateRangeFilterComponent implements OnInit {
    private eRef = inject(ElementRef);

    @Input() isMonthYear: boolean = false;
    @Input() formGroup!: FormGroup;
    readonly dateRangeChanged = output<any>();
    @Input() labelText: string = 'Select Date Range';

    @Input()
    get startDate(): Date | null {
        const begin = this.formGroup.get('beginDate')?.value;

        if (!begin) {
            return null;
        }

        return new Date(begin + 'T00:00:00');
    }

    set startDate(value: Date | null) {
        if (value) {
            this.updateStartDate(value);
        }
    }

    @Input()
    get endDate(): Date | null {
        const end = this.formGroup.get('endDate')?.value;

        if (!end) {
            return null;
        }

        return new Date(end + 'T00:00:00');
    }

    set endDate(value: Date | null) {
        if (value) {
            this.updateEndDate(value);
        }
    }

    selectedRange: { start: Date | null; end: Date | null } | null = {
        start: null,
        end: null,
    };

    monthRange: { start: Date | null; end: Date | null } = {
        start: null,
        end: null,
    };
    readonly popup = viewChild<any>('popup');
    readonly popupRef = viewChild.required('popup', { read: ElementRef });
    readonly anchor = viewChild.required<ElementRef>('anchorInput');

    ngOnInit(): void {
        const begin = this.formGroup.get('beginDate')?.value;
        const end = this.formGroup.get('endDate')?.value;
        if (begin && end) {
            this.selectedRange = {
                start: new Date(begin + 'T00:00:00'),
                end: new Date(end + 'T00:00:00'),
            };
            this.monthRange = this.selectedRange;
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        // Kendo renders the popup content (calendar, nav buttons, "Today" link, etc.)
        // in an animation container appended to the document body, outside of this
        // component's own DOM subtree, so we cannot rely on ElementRef.contains().
        // Instead, detect containment via known Kendo popup/calendar wrapper classes.
        const clickedInsideAnchor =
            !!this.anchor()?.nativeElement?.contains(target);
        const clickedInsidePopup = !!target?.closest(
            '.k-animation-container, .k-calendar-container, .k-daterange-popup, kendo-multiviewcalendar, .k-calendar',
        );

        const hidePopup = !clickedInsideAnchor && !clickedInsidePopup;

        if (hidePopup) {
            // Click happened outside both input and popup — close popup
            this.popup().toggle(false);
        }
    }

    onDateRangeChange(
        range: { start: Date; end: Date } | null,
        popup: any,
    ): void {
        if (range && range.start && range.end) {
            this.updateFormValues(range);
            //popup?.toggle();
        }
    }
    parseDateRange(value: string): { start: Date; end: Date } | null {
        if (!value) return null;

        const parts = value.split('-').map((p) => p.trim());

        // Case 1: Date range e.g., "01/01/2025 - 01/04/2025"
        if (parts.length === 2) {
            const firstPart = parts[0];
            const secondPart = parts[1];

            // Detect if format is MM/YYYY
            const monthYearRegex = /^\d{1,2}\/\d{4}$/;

            if (
                monthYearRegex.test(firstPart) &&
                monthYearRegex.test(secondPart)
            ) {
                const [startMonth, startYear] = firstPart
                    .split('/')
                    .map(Number);
                const [endMonth, endYear] = secondPart.split('/').map(Number);

                if (
                    startMonth < 1 ||
                    startMonth > 12 ||
                    endMonth < 1 ||
                    endMonth > 12
                )
                    return null;

                const start = new Date(startYear, startMonth - 1, 1);
                const end = new Date(endYear, endMonth, 0); // Last day of end month

                return { start, end };
            } else {
                // Fallback to default date parsing for full dates
                const start = new Date(firstPart);
                const end = new Date(secondPart);

                if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                    return null;
                }

                return { start, end };
            }
        }
        // Invalid format
        return null;
    }

    onManualBlur(event: Event): void {
        const input = event.target as HTMLInputElement;
        const value = input.value;
        const range = this.parseDateRange(value);
        if (range) {
            if (this.isMonthYear) this.monthRange = range;
            else this.selectedRange = range;

            this.onDateRangeChange(range, null);
        } else {
            // Optionally show error or revert to previous value
        }
    }

    updateStartDate(date: Date): void {
        const beginDate = formatDate(date, 'yyyy-MM-dd', 'en-US');
        this.formGroup.patchValue({
            beginDate,
        });
        this.emitDateRangeIfComplete();
    }

    updateEndDate(date: Date): void {
        const endDate = formatDate(date, 'yyyy-MM-dd', 'en-US');
        this.formGroup.patchValue({
            endDate,
        });
        this.emitDateRangeIfComplete();
    }

    updateFormValues(range: { start: Date; end: Date }): void {
        const beginDate = formatDate(range.start, 'yyyy-MM-dd', 'en-US');
        const endDate = formatDate(range.end, 'yyyy-MM-dd', 'en-US');
        this.formGroup.patchValue({
            beginDate,
            endDate,
        });
        this.emitDateRangeIfComplete();
    }

    // Only notify consumers once both ends of the range are populated,
    // preventing partial/incomplete emissions (e.g. only a start date set,
    // or an end date set while the previous start date is stale/null) from
    // propagating to the dashboard.
    private emitDateRangeIfComplete(): void {
        const begin = this.formGroup.get('beginDate')?.value;
        const end = this.formGroup.get('endDate')?.value;

        if (begin && end) {
            this.dateRangeChanged.emit(this.formGroup.value);
        }
    }

    toUSDate(dateValue: any, monthYear = false): string {
        const d = new Date(dateValue);
        const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(d.getUTCDate()).padStart(2, '0');
        const yyyy = d.getUTCFullYear();
        return monthYear ? `${mm}/${yyyy}` : `${mm}/${dd}/${yyyy}`;
    }

    // Kendo's multiviewcalendar (selection: 'range') natively manages the
    // two-click start/end selection flow itself and emits the resulting
    // { start, end } object via (valueChange) — it does NOT emit a single
    // Date. Treating this value as anything other than that range object
    // (as the previous implementation did) corrupts the two-way bound
    // monthRange, leading to errors like "this.value.start?.getTime is not
    // a function" on Kendo's next internal render/comparison.
    onMonthClick(
        range: { start: Date | null; end: Date | null },
        popup: any,
    ): void {
        this.monthRange = range;

        if (range?.start && range?.end) {
            this.updateFormValues({ start: range.start, end: range.end });
            setTimeout(() => popup.toggle(false), 100);
        }
    }

    getDisplayDateRange(): string {
        const begin = this.formGroup.get('beginDate')?.value;
        const end = this.formGroup.get('endDate')?.value;
        if (!begin || !end) return '';

        // Convert 'yyyy-MM-dd' strings to Date objects AND Append 'T00:00:00' to Force Local Time Interpretation
        const beginDate = new Date(begin + 'T00:00:00');
        const endDate = new Date(end + 'T00:00:00');

        if (isNaN(beginDate.getTime()) || isNaN(endDate.getTime())) return '';

        // Desired format
        const format = this.isMonthYear ? 'MM/yyyy' : 'MM/dd/yyyy';
        return `${formatDate(beginDate, format, 'en-US')} - ${formatDate(endDate, format, 'en-US')}`;
    }
}
